import { 
  ColumnActionsMode,
  DefaultButton, 
  Dropdown, 
  IColumn, 
  IconButton, 
  IDropdownOption, 
  MessageBarType, 
  Text,
  TextField,
  Toggle,
} from '@fluentui/react';
import classNames from 'classnames';
import { FC, FormEvent, ReactElement, useEffect, useState } from 'react';
import { IOOSReconciliationProps } from './IOOSReconciliationProps';
import SeparatorGy from '../../../../../../SeparatorGy/SeparatorGy';
import styles from './OOSReconciliation.module.scss';
import apiService from '../../../../../../../api';
import useNotifications from '../../../../../../../hooks/useNotifications';
import { customerSelector, locationSelector } from '../../../../../../../redux/recordKeepingSlice';
import { useSelector } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';

import { IPaginationProps } from '../../../../../../Pagination/IPaginationProps';
import { pageSizes } from '../../../../../../../consts/recordKeeping';
import LoadingScreen from '../../../../../../LoadingScreen/LoadingScreen';
import { IOOSReconciliationState } from './IOOSReconciliationState';
import AutoCompleteField from '../../../../../../Common/Search/AutoCompleteField';
import Tooltip from '../../../../../../../shared/Tooltip';
import { oosReconTireDetailsCols } from './const';
import Pagination from '../../../../../../Pagination/Pagination';
import { IOOSReconciliationTire } from './OOSReconciliationTire';
import moment from 'moment';
import { downloadFile, printingTypes } from '../../../../../../PrintingModal/consts';
import PrintingModal from '../../../../../../PrintingModal/PrintingModal';
import { auth_tireDisposition_tireOptixQueue_noMatch } from '../../../../../../../consts/programKeys';
import { useUserPermissions } from '../../../../../../../hooks/useUserPermissions';
import { sortOrder } from '../../../../../../../consts/sortOrder';

const OOSReconciliation: FC<IOOSReconciliationProps> = ({
  fetchQueueRecords,
  shippingDetails: {
    shipToCustomerName,
    shipToLocCode,
  },
  surveyDetails: {
    docNum,
    tireOptixSurveyId,
    glsSurveyId,
    comments,
    dispCode,
    surveyDate,
    plantNo,
    plantName,
  },
  tireDetails: {
    tireId,
  },

}): ReactElement => {

  const [state, setState] = useState<IOOSReconciliationState>({
    items: [],
    selectedItems: [],
    loading: false,
    foundCount: 0,
  });
  const [columnsState, setColumnsState] = useState<Array<any>>(oosReconTireDetailsCols);
  const { addNotification } = useNotifications();
  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({ key: pageSizes[1], text: pageSizes[1].toString() });
  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) => setPaginationProps((prev: any) => ({ ...prev, current: newPage })),
  });

  const handleSelect = (e: any, itemId: any) => {
    if (e.target.checked) {
      setState((prev: any) => ({ ...prev, selectedItems: [...state.selectedItems, itemId] }));
    } else {
      setState((prev: any) => ({ ...prev, selectedItems: state.selectedItems.filter(row => row !== itemId) }));
    }
  };

  const handleSelectAll = (e: any, items: Array<any>) => {
    e.target.checked ? setState((prev: any) => ({ ...prev, selectedItems: items })) : setState((prev: any) => ({ ...prev, selectedItems: [] }));
  };

  const onChangeCountOnPage = (event: FormEvent<HTMLDivElement>, item: IDropdownOption<any> | undefined): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(({ isSorted }) => isSorted);
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  const actionsClassName = (item: any) => {
    if (item.isSorted && item.isSortedDescending){
      return styles.descending;
    } else if (item.isSorted && !item.isSortedDescending){
      return styles.ascending;
    } else {
      return undefined;
    }
  };

  const onColumnClick = (column: IColumn): void => {
    if (column.columnActionsMode == ColumnActionsMode.disabled) return;
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    setColumnsState(newColumns);
  };

  const { id: customerId, customerName, prefix } = useSelector(customerSelector);
  const { id: locationId, locationCode } = useSelector(locationSelector);
  const [newComments, setNewComments] = useState(comments);
  const [showPrintExport, { toggle: toggleShowPrintExport }] = useBoolean(false);
  const [viewErrors, setViewErrors] = useState<boolean>(false);

  const { hasPermission } = useUserPermissions();
  const userPermissionsTireDispoQueueNoMatch = hasPermission(auth_tireDisposition_tireOptixQueue_noMatch);

  const DATE_FORMAT = 'MM/DD/YYYY';
  const STATUS_COMPLETED = 'Completed';
  const STATUS_ERROR = 'Error';
  const STATUS_PENDING = 'Pending';

  const formatDate = (date: string) => date ? moment(date).format(DATE_FORMAT) : null;

  const fetchTireDetails = async () => {
    setState(prev => ({...prev, loading:true}));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.tireDispositionQueueTires.get(
        customerId,
        locationId,
        tireOptixSurveyId ? Number.parseInt(tireOptixSurveyId, 10) : null,
        glsSurveyId,
        viewErrors,
        sortOrder,
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
      );
      const foundCount = data.total.found;
      const items = data.data.map((item:any) => ({...item, dateModified: formatDate(item.dateModified)}));
      if (tireId) {
        setState((prev: any) => ({...prev, selectedItems: [tireId] }));
      }
      else {
        setState((prev: any) => ({...prev, selectedItems: [] }));
      }
      setState((prev: any) => ({ ...prev, items, foundCount }));
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
    } catch (e: any) {
      addNotification({
        text: `OOS Reconciliation Tires fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const handleSubmit = async () => {
    setState(prev => ({...prev, loading:true}));
    try {
      saveComments();
      const listOfTires = state.items.filter(item => state.selectedItems.includes(item.tireOptixNo) && !isStatusComplete(item)).map(item => ({
        tireOptixNo: item.tireOptixNo,
        prefix: item.prefix,
        brandNo: item.brandNo,
        suffix: item.suffix,
        billableTreadDepth: Number(item.billableTreadDepth),
      }));
      await apiService.tireDispositionQueueTires.submit(
        comments, customerId, locationId, listOfTires, { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
      );
      addNotification({
        text: 'OOS Reconciliation tires were successfully submitted.',
        type: MessageBarType.success,
      });
    }
    catch (e:any) {
      const { response } = e;
      setState(prev => ({ ...prev, loading: false }));
      switch (response.status) {
        case 400:
          return addNotification({
            text: 'Submitting tires error: '+ response.data.state[0]?.message,
            type: MessageBarType.error,
          });
        case 499:
          return addNotification({
            text: response.data.desc.message,
            type: MessageBarType.error,
          });
        default:
          return addNotification({
            text: response.data.message,
            type: MessageBarType.error,
          });
      }
    } finally {
      if (paginationProps.current === 1) {
        await fetchTireDetails();
      } else {
        await paginationProps.onChangePage(1);
      }
      fetchQueueRecords();
    }
  };

  const toggleNoMatch = async () => {
    setState(prev => ({...prev, loading:true}));

    try {
      const listOfTires = state.items.filter(item => state.selectedItems.includes(item.tireOptixNo)).map(item => ({
        tireOptixNo: item.tireOptixNo,
        noMatch: isNoMatch(item) ? null : 'Yes', //toggle no match
      }));
      await apiService.tireDispositionQueueTires.markAsNoMatch(
        customerId, locationId, listOfTires, glsSurveyId, { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
      );
      addNotification({
        text: 'Tires marked/unmarked as No Match',
        type: MessageBarType.success,
      });
    }
    catch (e:any) {
      const { response } = e;
      setState(prev => ({ ...prev, loading: false }));
      switch (response.status) {
        case 400:
          return addNotification({
            text: 'Unable to mark/unmark as No Match',
            type: MessageBarType.error,
          });
        case 499:
          return addNotification({
            text: response.data.desc.message,
            type: MessageBarType.error,
          });
        default:
          return addNotification({
            text: response.data.message,
            type: MessageBarType.error,
          });
      }
    } finally {
      if (paginationProps.current === 1) {
        await fetchTireDetails();
      } else {
        await paginationProps.onChangePage(1);
      }
      fetchQueueRecords();
    }
  };

  const updateComments = (event:any) => {
    setNewComments(event.target.value);
  };

  const saveComments = () => {
    if (comments !== newComments) {
      comments = newComments;
      setNewComments(comments);
    } 
  };

  const submitComments = async () => {
    setState(prev => ({...prev, loading:true}));
    try {
      saveComments();
      await apiService.tireDispositionQueue.saveComments(
        comments,
        customerId,
        locationId,
        glsSurveyId,
        tireOptixSurveyId,
      );
      addNotification({
        text: 'Comments were successfully updated.',
        type: MessageBarType.success,
      });
    } catch (e: any) {
      addNotification({
        text: 'Comments updating error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      fetchQueueRecords();
    }
  };

  const setTireToBeEdited = (id: string, field: string, value: any, regExp?: RegExp) => {
    if (!regExp || (regExp?.test(value))) {
      setState((prev: any) => ({
        ...prev,
        items: prev.items.map((item: IOOSReconciliationTire) => item.id === id ?  { ...item, [field]: value } : item),
      }));
    }
    if (field === 'prefix') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.prefix = value);
    }
    else if (field === 'brandNo') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.brandNo = value);
    }
    else if (field === 'suffix') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.suffix = value);
    }
    else if (field === 'billableTreadDepth') {
      state.items.filter(item => item.tireOptixNo === id).map(item => item.billableTreadDepth = value);
    }
  };

  const isPrefixMatching = (item: any) => {
    if (item.prefix !== item.ogPrefix && 
      !(item.prefix == null && item.ogPrefix === '') && 
      !(item.prefix === '' && item.ogPrefix == null)) {
      return true;
    } else {  
      return false;
    }
  };

  const isBrandMatching = (item: any) => {
    if (item.brandNo !== item.ogBrandNo && 
      !(item.brandNo == null && item.ogBrandNo === '') && 
      !(item.brandNo === '' && item.ogBrandNo == null)) {
      return true;
    } else {  
      return false;
    }
  };

  const isSuffixMatching = (item: any) => {
    if (item.suffix !== item.ogSuffix && 
      !(item.suffix == null && item.ogSuffix === '') && 
      !(item.suffix === '' && item.ogSuffix == null)) {
      return true;
    } else {  
      return false;
    }
  };


  const handlePrint = async (printingType: any) => {
    setState(prev => ({ ...prev, loading: true }));
    toggleShowPrintExport();
    try {
      const tireOptixSurveyId = docNum ? Number.parseInt(docNum, 10) : null;
      const requestData = {
        customerId,
        locationId,
        tireOptixSurveyId,
        glsSurveyId,
        dispCode,
        viewErrors,
        pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        plantName,
        plantNo,
      };
      const { data }: any = printingType === printingTypes.excel ?
        await apiService.tireDispositionQueueTires.printExcel(requestData) :
        await apiService.tireDispositionQueueTires.printPdf(requestData);

      addNotification({
        text: 'File was successfully received.',
        type: MessageBarType.success,
      });
      downloadFile(data, printingType);
    } catch (e: any) {
      console.log(e);
      addNotification({
        text: 'Printing error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const isStatusComplete = (item: any) => {
    if (item.status === STATUS_COMPLETED)
      return true;
  };
  const isStatusError = (item: any) => {
    if (item.status === STATUS_ERROR)
      return true;
  };
  const isStatusPending = (item: any) => {
    if (item.status === STATUS_PENDING)
      return true;
  };
  const isNoMatch = (item: any) => {
    if (!item.noMatch) //if null
      return false;
    else
      return true;
  };

  const onChangeViewErrors = () => {
    if (viewErrors) {
      setViewErrors(false);
      paginationProps.current = 1;
    }
    else {
      setViewErrors(true);
      paginationProps.current = 1;
    }
  };

  const isSubmitButtonDisabled = () => {
    // if tires are selected and are not no match, then enable submit button
    // user should be able to submit only tires that are not no match
    // select bubble is still enabled to allow user to select tires that are no match and toggle
    const listOfTires = state.items.filter(item => state.selectedItems.includes(item.tireOptixNo));
    const isSelectedNoMatch = listOfTires.filter(item => isNoMatch(item)).length > 0;
    if (!isSelectedNoMatch && state.selectedItems.length > 0) {
      return false;
    }
    else if (isSelectedNoMatch || state.selectedItems.length === 0)
      return true;
    else
      return true;
  };

  const isToggleButtonDisabled = () => {
    // should only toggle if tire is not pending or based on toggle conditions above
    const listOfTires = state.items.filter(item => state.selectedItems.includes(item.tireOptixNo));
    const isSelectedPending = listOfTires.filter(item => isStatusPending(item)).length > 0;
    if (!isSelectedPending && state.selectedItems.length > 0) {
      return false;
    }
    else if (isSelectedPending || state.selectedItems.length === 0)
      return true;
    else
      return true;
  };

  useEffect(() => {
    fetchTireDetails();
  }, [
    paginationProps.current,
    countOnPage,
    columnsState,
    customerId,
    locationId,
    tireOptixSurveyId,
    glsSurveyId,
    viewErrors,
  ]);

  useEffect(() => {
    return () => {
      // Reset selected items when re-opening the modal
      setState(prev => ({
        ...prev,
        selectedItems: [],
      }));
    };
  }, []);

  return (
    <>
      <div className={styles.customerInfo}>
        <div className="ms-Grid">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
              <AutoCompleteField
                initialValue={prefix}
                list={[]}
                label="Customer Prefix"
                data-testid='customer-prefix'
                icon="FavoriteStar"
                disabled
              />
              <AutoCompleteField
                initialValue={customerName}
                list={[]}
                label="Customer Name"
                data-testid='customer-name'
                icon="Contact"
                disabled
              />
              <AutoCompleteField
                initialValue={locationCode}
                list={[]}
                label="Location Code"
                data-testid='location-code'
                icon="MapPin"
                disabled
              />
              <AutoCompleteField
                initialValue={plantName}
                list={[]}
                label="Plant Name"
                data-testid='plant-name'
                icon="FavoriteStar"
                disabled
              />
            </div>
            <div className="ms-Grid-col ms-sm2 ms-md2 ms-lg2">
              <AutoCompleteField
                initialValue={docNum}
                list={[]}
                label="Tire Optix Survey ID"
                data-testid='to-survey-id'
                icon="FavoriteStar"
                disabled
              />
              <AutoCompleteField
                initialValue={glsSurveyId}
                list={[]}
                label="GLS Survey ID"
                data-testid='gls-survey-id'
                icon="FavoriteStar"
                disabled
              />
              <AutoCompleteField
                initialValue={surveyDate}
                list={[]}
                label="Survey Date"
                data-testid='survey-date'
                icon="DateTime"
                disabled
              />
              <AutoCompleteField
                initialValue={plantNo}
                list={[]}
                label="Plant No"
                data-testid='plant-no'
                icon="FavoriteStar"
                disabled
              />
            </div>
            <div className="ms-Grid-col">
              <AutoCompleteField
                initialValue={shipToCustomerName}
                list={[]}
                label="Ship To Customer Name"
                data-testid='ship-to-customer-name'
                icon="DeliveryTruck"
                disabled
              />
              <AutoCompleteField
                initialValue={shipToLocCode}
                list={[]}
                label="Ship To Location Code"
                data-testid='ship-to-location-code'
                icon="DeliveryTruck"
                disabled
              />
            </div>
            <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
              <TextField
                value={newComments}
                label="Comments"
                data-testid='comments'
                multiline rows={8}
                onChange={updateComments}
              />
            </div>
            <div className={styles.lastColumn}>
              <div className="ms-Grid-col">
                <DefaultButton
                  id="saveComments"
                  text="Save Comments"
                  onClick={submitComments}
                />
                <Toggle
                  label="Show Only Errors"
                  checked={viewErrors}
                  onChange={onChangeViewErrors}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.tableHeading}>
        <div>
          <Text variant="xLarge" className={styles.highlight}>Tires</Text>
          <SeparatorGy vertical />
          <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
        </div>
        <div className={styles.buttonsWrapper}>
          <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
          <Dropdown
            options={pageSizes.map(pageSize => ({
              key: pageSize,
              text: pageSize.toString(),
            }))}
            defaultSelectedKey={pageSizes[0]}
            selectedKey={countOnPage?.key}
            onChange={onChangeCountOnPage}
          />
          <DefaultButton
            id="export"
            text="Export"
            onClick={toggleShowPrintExport}
          />
          <IconButton
            id="refreshButton"
            iconProps={{ iconName: 'Refresh' }} 
            onClick={fetchTireDetails} 
          />
        </div>
      </div>
      <div className={styles['table-wrapper']}>
        <table className={styles['oos-table']}>
          <thead>
            <tr>
              {
                columnsState.map(item => (
                  <th
                    key={item.key}
                    className={
                      actionsClassName(item)
                    }
                    onClick={() => onColumnClick(item)}
                  >
                    {item.name}
                    {item.tooltip ? <Tooltip 
                      content={item.tooltip}
                    />:null}
                  </th>
                ))
              }
              <th>
                { state.items.filter(item => !isStatusComplete(item) || isNoMatch(item)).length > 0 ? 
                  <div className={styles.round}>
                    <input
                      type="checkbox"
                      id="allTires"
                      checked={state.items.length !== 0 && (state.items.filter(item => !isStatusComplete(item) || isNoMatch(item)).length == state.selectedItems.length)}
                      onChange={(e) => handleSelectAll(e, state.items.filter(item => !isStatusComplete(item) || isNoMatch(item)).map(({ tireOptixNo }) => tireOptixNo))}
                    />
                    <label htmlFor="allTires">{''}</label>
                  </div> : null }
              </th>
            </tr>
          </thead>
          <tbody>
            {
              state.items.map(item => (
                <tr key={item.tireOptixNo}
                  className={
                    classNames(
                      styles.trBasic,
                      {
                        [styles.trSelected]: state.selectedItems.includes(item.tireOptixNo),
                      },
                      {
                        [styles.trError]: isStatusError(item),
                      },
                      {
                        [styles.trComplete]: isStatusComplete(item),
                      },
                    )
                  }
                >
                  <td>
                    <TextField
                      value={item.prefix}
                      onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'prefix', a?.toUpperCase())}
                      id={item.tireOptixNo+'_prefix'}
                      disabled = {isStatusComplete(item)}
                      styles={{
                        fieldGroup: { 
                          background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                        },
                      }}
                    />
                    {isPrefixMatching(item) ? 
                      <Tooltip content={'Tire Optix: '+item.ogPrefix}/> : null
                    }
                  </td>
                  <td>
                    <TextField
                      defaultValue={item.brandNo}
                      onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'brandNo', a)}
                      id={item.tireOptixNo+'_brandNo'}
                      disabled = {isStatusComplete(item)}
                      styles={{
                        fieldGroup: { 
                          background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                        },
                      }}
                    />
                    {isBrandMatching(item) ? 
                      <Tooltip content={'Tire Optix: '+item.ogBrandNo}/> : null
                    }
                  </td>
                  <td>
                    <TextField
                      value={item.suffix}
                      onChange={(e, a) => setTireToBeEdited(item.tireOptixNo, 'suffix', a?.toUpperCase())}
                      id={item.tireOptixNo+'_suffix'}
                      disabled = {isStatusComplete(item)}
                      styles={{
                        fieldGroup: { 
                          background: isStatusComplete(item) ? '#f3f2f1' : '#ffffff',
                        },
                      }}
                    />
                    {isSuffixMatching(item) ? 
                      <Tooltip content={'Tire Optix: '+item.ogSuffix}/> : null
                    }
                  </td>
                  <td>{item.tireSize}</td>
                  <td>{item.inspectionCode}</td>
                  <td>{item.dispCode}</td>
                  <td>{item.noMatch ? 'Yes' : null}</td>
                  <td>{item.status} {(isStatusError(item) && item.errMsg) ? <Tooltip 
                    content={item.errMsg}
                  />:null}</td>
                  <td>{item.vehicleNo}</td>
                  <td>{item.glsWhlPos}</td>
                  <td>{item.dateModified}</td>
                  <td>{item.prevVehicleNo}</td>
                  <td>{item.prevWheelPos}</td>
                  <td>{item.treadDepth}</td>
                  <td><TextField
                    value={item.billableTreadDepth}
                    onChange={(e, newValue) => {
                      if (/^\d*$/.test(newValue ?? '')) {
                        setTireToBeEdited(item.tireOptixNo, 'billableTreadDepth', newValue);
                      }
                    }}
                    id={item.tireOptixNo+'_billableTreadDepth'}
                    disabled = {isStatusComplete(item) || !item.isBillableByTD}
                  /></td>
                  <td>{item.totMiles}</td>
                  <td>{item.comments}</td>
                  <td>{item.images ? <a href={item.images} target='_blank'>View</a> : null}</td>
                  <td>{item.lastModifiedBy}</td>
                  <td>
                    {!isStatusComplete(item) || isNoMatch(item) ?
                      <div className={styles.round}>
                        <input type="checkbox" id={item.tireOptixNo} checked={state.selectedItems.includes(item.tireOptixNo)} onChange={(e) => handleSelect(e, item.tireOptixNo)} />
                        <label htmlFor={item.tireOptixNo}>{''}</label>
                      </div> : null}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <div className={styles.detailsRow}>
        <div className={styles.detailsColumn}>
          <div className={styles.fieldsActions}>
            {userPermissionsTireDispoQueueNoMatch.isAccess && <DefaultButton
              id="markAsNoMatch"
              text="Toggle No Match"
              onClick={toggleNoMatch}
              disabled={isToggleButtonDisabled()} //disable if no tires selected && status != pending
            />}
            <DefaultButton
              id="submit"
              text="Submit"
              onClick={handleSubmit}
              disabled={isSubmitButtonDisabled()}
            />
          </div>
        </div>
      </div>
      {state.loading && <LoadingScreen />}
      <Pagination {...paginationProps} />
      <PrintingModal
        isOpened={showPrintExport}
        onClose={toggleShowPrintExport}
        onPrint={handlePrint}
      />
    </>
  );
};

export default OOSReconciliation;